<template>
  <div id="form-wrapper" class="issue-form__wrapper">
    <FormThankYouOverlay
      :is-error="isFailed"
      :is-loading="isLoading"
      :is-success="isFormSent"
    >
      <img
        src="/images/thank-you-hand.png"
        class="issue-form__image"
        alt="Thank you illustration"
      />
      <h2 class="issue-form__heading">Thank you!</h2>
      <p class="issue-form__text">
        Your message has been sent. We will contact you after checking your
        report.
      </p>
    </FormThankYouOverlay>
    <FormErrorMessage v-if="isFailed" />
    <form
      id="issue-form"
      novalidate
      class="issue-form"
      @submit.prevent="onSubmit"
    >
      <FormBaseInput
        v-model="state.name"
        :placeholder="$t('common.form.fields.full_name.placeholder')"
        :disabled="isLoading"
        type="text"
        name="name"
        required
        :v="v$.name"
        :label="$t('common.form.fields.full_name.reporter')"
      />
      <FormBaseInput
        v-model="state.email"
        :placeholder="$t('common.form.fields.email.placeholder')"
        :disabled="isLoading"
        type="email"
        name="email"
        required
        :v="v$.email"
        :label="$t('common.form.fields.email.reporter')"
      />
      <FormBaseTextarea
        v-model="state.message"
        :placeholder="$t('common.form.fields.vulnerability.placeholder')"
        :disabled="isLoading"
        name="message"
        required
        :v="v$.message"
        :label="$t('common.form.fields.vulnerability.label')"
      />
      <div class="issue-form__consent">
        <FormBaseCheckbox
          v-model="state.consentToUseData"
          class="checkbox-group__consent"
          required
          label-i18-path="common.form.consents.legitimate_interest"
          name="consent-to-use-data"
          :v="v$.consentToUseData"
        >
          <nuxt-link to="/privacy-policy">
            {{ $t('common.footer.privacy_policy') }}
          </nuxt-link>
          <a href="mailto:dpo@infermedica.com"> dpo@infermedica.com </a>
        </FormBaseCheckbox>
      </div>
      <BaseButton
        :title="$t('common.form.send')"
        class="button--submit-form issue-form__submit"
        type="submit"
      >
        <template #text>
          <span>{{ $t('common.form.send') }}</span>
        </template>
      </BaseButton>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { useVuelidate } from '@vuelidate/core'
import useValidation from '@/composables/useValidation'
import useSendData from '@/composables/useSendData'

import { getCookie } from '@/utils/helpers'

const config = useRuntimeConfig()

const state = reactive({
  name: '',
  email: '',
  message: '',
  consentToUseData: false,
})

const isFormSent = ref(false)

const route = useRoute()

const { t } = useI18n()

const { isFieldRequired, isEmailValid, isConsentAccepted, isEmailBusiness } =
  useValidation()

const rules = {
  name: {
    required: isFieldRequired,
  },
  email: {
    required: isFieldRequired,
    email: isEmailValid,
    business: isEmailBusiness,
  },
  message: {
    required: isFieldRequired,
  },
  consentToUseData: {
    required: isConsentAccepted,
  },
}

const { scrollToError, isFailed, isLoading, sendFormData } = useSendData()

const v$ = useVuelidate(rules, state)

const onSubmit = async () => {
  v$.value.$touch()

  if (v$.value.$invalid) {
    return
  }

  const data = {
    submittedAt: Date.now(),
    fields: [
      {
        name: 'email',
        value: state.email,
      },
      {
        name: 'firstname',
        value: state.name,
      },
      {
        name: 'message',
        value: state.message,
      },
    ],
    context: {
      hutk: getCookie('hubspotutk'),
      pageUri: window.location.href,
      pageName: route.name,
    },
    legalConsentOptions: {
      consent: {
        consentToProcess: state.consentToUseData,
        text: t('common.form.consents.legitimate_interest'),
      },
    },
  }

  if (!v$.value.$invalid) {
    await sendFormData(config.public.hubSpot.reportIssueFormUrl, data, {
      thankYouRoute: '',
    })
  }

  await nextTick()

  if (isFailed.value) {
    scrollToError()
    return
  }

  isFormSent.value = true
}
</script>

<style lang="scss" scoped>
.issue-form {
  &__submit {
    margin: 0 auto;
    display: block;
  }

  &__consent {
    margin-bottom: 40px;
  }

  &__image {
    width: 120px;
    height: auto;
    margin-bottom: 32px;
  }

  &__heading {
    margin-bottom: 12px;
  }

  &__text {
    text-align: center;
    max-width: 400px;
  }
}
</style>
